import React from "react";
import Slider from "react-slick";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Rating,
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import ApartmentIcon from "@mui/icons-material/Apartment";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const royalBlue = {
  main: "#4169E1",
  dark: "#183A9E",
  light: "#6F8EF6",
  soft: "#EEF3FF",
  border: "rgba(65, 105, 225, 0.18)",
  text: "#102A71",
  white: "#FFFFFF",
};

const PrevArrow = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: "absolute",
      left: { xs: 8, md: -18 },
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 5,
      width: 48,
      height: 48,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "rgba(255,255,255,0.16)",
      backdropFilter: "blur(12px)",
      border: `1px solid ${royalBlue.border}`,
      color: royalBlue.white,
      cursor: "pointer",
      boxShadow: "0 14px 32px rgba(24,58,158,0.30)",
      transition: "all 0.3s ease",
      "&:hover": {
        bgcolor: royalBlue.light,
        transform: "translateY(-50%) scale(1.08)",
      },
    }}
  >
    &#8592;
  </Box>
);

const NextArrow = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: "absolute",
      right: { xs: 8, md: -18 },
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 5,
      width: 48,
      height: 48,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "rgba(255,255,255,0.16)",
      backdropFilter: "blur(12px)",
      border: `1px solid ${royalBlue.border}`,
      color: royalBlue.white,
      cursor: "pointer",
      boxShadow: "0 14px 32px rgba(24,58,158,0.30)",
      transition: "all 0.3s ease",
      "&:hover": {
        bgcolor: royalBlue.light,
        transform: "translateY(-50%) scale(1.08)",
      },
    }}
  >
    &#8594;
  </Box>
);

function ClientStory() {
  const settings = {
    dots: true,
    infinite: true,
    speed: 900,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3200,
    pauseOnHover: true,
    cssEase: "ease-in-out",
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const clientReports = [
    {
      name: "Mr. Krishnan",
      location: "Korattur",
      category: "Individual House",
      size: "5000 Sq.ft",
      structure: "Stilt + 2 Floors",
      review:
        "We are happy with the team’s transparent communication and structured execution. Our individual house project was handled with care, commitment, and very good coordination throughout.",
    },
    {
      name: "Mr. G. Anil Agarwal",
      location: "Kilpauk Garden",
      category: "Residential",
      size: "10000 Sq.ft",
      structure: "Stilt + 3 Floors",
      review:
        "The project was executed with professionalism and strong planning from start to finish. We appreciated the timely updates, quality work, and respectful approach of the entire team.",
    },
    {
      name: "Mr. Ganesh",
      location: "Mogappair",
      category: "Residential",
      size: "2400 Sq.ft",
      structure: "Ground + 2 Floors",
      review:
        "A smooth and trustworthy construction experience overall. The team understood our needs clearly and delivered a neat residential project with good workmanship and discipline.",
    },
    {
      name: "Mr. D.V. Balachandran",
      location: "Nolambur Phase-2",
      category: "Independent Duplex House",
      size: "6000 Sq.ft",
      structure: "Stilt + 3 Floors",
      review:
        "Their technical support and site management gave us real confidence during the duplex house construction. The work quality, clarity, and commitment were impressive at every stage.",
    },
    {
      name: "Mr. Sudharsan Khemka",
      location: "Redhills",
      category: "Commercial Warehouse",
      size: "12000 Sq.ft",
      structure: "Commercial Project",
      review:
        "The warehouse project was managed with dependable supervision and professional execution. We are satisfied with the practical planning, site control, and strong delivery standards.",
    },
    {
      name: "Mr. Sudharsanan Khemka",
      location: "Allamathi",
      category: "Warehouse",
      size: "6000 Sq.ft",
      structure: "Commercial Project",
      review:
        "The team delivered our warehouse project with clarity, transparency, and consistent follow-up. Their approach was practical and quality-driven, which made the process comfortable for us.",
    },
    {
      name: "Veyem Engineering Service",
      location: "Royapuram / Egmore / Chetpet Railway Station",
      category: "Southern Railway Building Construction Contract Work",
      size: "Building Construction",
      structure: "Contract Project",
      review:
        "We value the team’s accountability and execution quality on contract-based work. Their coordination and commitment to standards made the collaboration reliable and efficient.",
    },
    {
      name: "Chakra Foundation",
      location: "Srinivasa Milk Product, Kallikuppam",
      category: "Commercial Projects",
      size: "10000 Sq.ft",
      structure: "G + 3 Floor",
      review:
        "Our commercial works were completed with a professional attitude and clear communication. The team maintained quality, timely responses, and a polished execution process throughout.",
    },
    {
      name: "Chakra Foundation",
      location: "Pudhur",
      category: "Commercial Projects",
      size: "6000 Sq.ft",
      structure: "Ground + 3 Floor",
      review:
        "Our commercial works were completed with a professional attitude and clear communication. The team maintained quality, timely responses, and a polished execution process throughout.",
    },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        px: { xs: 1, md: 4 },
        py: 4,
        ".slick-slide": {
          padding: "16px",
        },
        ".slick-track": {
          display: "flex",
        },
        ".slick-slide > div": {
          height: "100%",
        },
        ".slick-dots li button:before": {
          fontSize: "12px",
          color: royalBlue.main,
          opacity: 0.35,
        },
        ".slick-dots li.slick-active button:before": {
          color: royalBlue.dark,
          opacity: 1,
        },
      }}
    >
      <Slider {...settings}>
        {clientReports.map((client, index) => (
          <Box key={index}>
            <Card
              sx={{
                minHeight: 380,
                borderRadius: 5,
                overflow: "hidden",
                position: "relative",
                background:
                  "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(238,243,255,0.96))",
                boxShadow: "0 22px 60px rgba(24,58,158,0.16)",
                border: `1px solid ${royalBlue.border}`,
                transition: "all 0.45s ease",
                "&:hover": {
                  transform: "translateY(-10px)",
                  boxShadow: "0 28px 70px rgba(24,58,158,0.22)",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "6px",
                  background: `linear-gradient(90deg, ${royalBlue.dark}, ${royalBlue.main}, ${royalBlue.light})`,
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      background: `linear-gradient(135deg, ${royalBlue.main}, ${royalBlue.dark})`,
                      width: 58,
                      height: 58,
                      fontWeight: "bold",
                      boxShadow: "0 12px 28px rgba(24,58,158,0.28)",
                    }}
                  >
                    {client.name?.charAt(0)}
                  </Avatar>

                  <FormatQuoteIcon
                    sx={{
                      fontSize: 42,
                      color: "rgba(65,105,225,0.16)",
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: royalBlue.text, mb: 0.5 }}
                >
                  {client.name}
                </Typography>

                <Rating value={5} readOnly size="small" sx={{ mb: 2, color: royalBlue.main }} />

                <Typography
                  variant="body1"
                  sx={{
                    color: "#46506b",
                    lineHeight: 1.9,
                    mb: 3,
                    fontStyle: "italic",
                    minHeight: 110,
                  }}
                >
                  “{client.review}”
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.2,
                    mb: 2,
                  }}
                >
                  <Chip
                    icon={<LocationOnIcon />}
                    label={client.location}
                    sx={{
                      bgcolor: royalBlue.soft,
                      color: royalBlue.dark,
                      fontWeight: 600,
                      border: `1px solid ${royalBlue.border}`,
                    }}
                  />
                  <Chip
                    icon={<HomeWorkIcon />}
                    label={client.category}
                    sx={{
                      bgcolor: "#f8faff",
                      color: royalBlue.text,
                      border: `1px solid ${royalBlue.border}`,
                    }}
                  />
                  <Chip
                    icon={<SquareFootIcon />}
                    label={client.size}
                    sx={{
                      bgcolor: "#f8faff",
                      color: royalBlue.text,
                      border: `1px solid ${royalBlue.border}`,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${royalBlue.border}`,
                  }}
                >
                  <ApartmentIcon sx={{ color: royalBlue.main }} />
                  <Typography variant="body2" sx={{ color: "#5a6480" }}>
                    {client.structure}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Slider>
    </Box>
  );
}

export default ClientStory;
